"use client";

import { useState } from "react";
import Link from "next/link";
import { NewsMarkdownRenderer } from "@/components/ui/common/NewsMarkdownRenderer";
import { Button } from "@/components/ui/shadcn/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/shadcn/dialog";

interface Announcement {
  id: string;
  title: string;
  content: string;
  severity: "info" | "warning" | "critical";
  version: number;
  publishedAt: string | null; // Can be null for draft announcements, though we filter those out
  createdAt: string;
}

interface InfoSplashModalProps {
  open: boolean;
  announcements: Announcement[];
  onAcknowledge?: () => void; // Marks all as read (list mode)
  onAcknowledgeSingle?: (announcementId: string) => void; // Marks one as read (carousel mode)
  onClose: () => void;
  mode?: "list" | "carousel"; // Display mode
}

const severityConfig = {
  info: {
    label: "Info",
    className: "bg-muted text-foreground border-border",
    borderColor: "border-l-blue-500/60",
  },
  warning: {
    label: "Warning",
    className:
      "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning-border))]",
    borderColor: "border-l-yellow-500/60",
  },
  critical: {
    label: "Critical",
    className:
      "bg-[hsl(var(--error-bg))] text-[hsl(var(--error))] border-[hsl(var(--error-border))]",
    borderColor: "border-l-red-500/70",
  },
};

export function InfoSplashModal(props: InfoSplashModalProps) {
  // Use a key that changes when the modal opens to reset internal state.
  // `announcements.length` is included so the inner component re-mounts
  // if announcements change while the modal is closed.
  const resetKey = `${props.open}-${props.announcements.length}`;

  return <InfoSplashModalInner key={resetKey} {...props} />;
}

function InfoSplashModalInner({
  open,
  announcements,
  onAcknowledge,
  onAcknowledgeSingle,
  onClose,
  mode = "list",
}: InfoSplashModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewedAnnouncementIds, setViewedAnnouncementIds] = useState<Set<string>>(new Set());

  // Capture announcement count at mount time (when modal opens)
  const originalCount = announcements.length;

  const isCarouselMode = mode === "carousel";
  const currentAnnouncement = isCarouselMode ? announcements[currentIndex] : null;
  const isFirstAnnouncement = currentIndex === 0;
  // Use originalCount for last announcement check to handle items being marked as read
  const isLastAnnouncement = currentIndex === originalCount - 1;

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    // Track current announcement as viewed (but don't mark as read yet)
    if (isCarouselMode && currentAnnouncement) {
      setViewedAnnouncementIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentAnnouncement.id);
        return newSet;
      });
    }

    if (isLastAnnouncement) {
      // Mark all viewed announcements as read before closing
      handleCarouselClose();
    } else {
      // Move to next announcement
      setCurrentIndex(currentIndex + 1);
    }
  };

  const markViewedAsRead = () => {
    // Mark all viewed announcements as read in carousel mode
    if (isCarouselMode && onAcknowledgeSingle) {
      viewedAnnouncementIds.forEach((id) => onAcknowledgeSingle(id));
      // Also mark the current announcement if we're viewing it
      if (currentAnnouncement) {
        onAcknowledgeSingle(currentAnnouncement.id);
      }
    }
  };

  const handleListAcknowledge = () => {
    if (onAcknowledge) {
      onAcknowledge();
    }
    onClose();
  };

  const handleCarouselClose = () => {
    // Mark all viewed announcements as read before closing
    markViewedAsRead();
    onClose();
  };

  // Handle dialog close - only allow backdrop/ESC close in list mode
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Dialog is closing
      if (mode === "list") {
        handleListAcknowledge();
      }
      // In carousel mode, prevent backdrop/ESC close - user must click navigation
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-full max-w-4xl h-[75vh] sm:h-[70vh] flex flex-col bg-background shadow-2xl p-0 gap-0 [&>button]:hidden"
        onPointerDownOutside={(e) => {
          // Prevent backdrop click close in carousel mode
          if (mode === "carousel") {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent ESC key close in carousel mode
          if (mode === "carousel") {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        <DialogHeader className="border-b border-border/40 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 relative flex-shrink-0 space-y-1">
          <DialogTitle className="text-lg sm:text-xl font-semibold text-foreground pr-8">
            {isCarouselMode ? "New Announcement" : "News"}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
            {isCarouselMode
              ? `${currentIndex + 1} of ${originalCount}`
              : `${announcements.length} ${announcements.length === 1 ? "announcement" : "announcements"}`}
          </DialogDescription>
          {/* X Close button - always visible as failsafe */}
          <button
            onClick={handleCarouselClose}
            className="absolute right-4 sm:right-6 lg:right-8 top-3 sm:top-4 p-1 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X size={18} className="sm:w-5 sm:h-5" />
          </button>
        </DialogHeader>

        {/* Content Area */}
        {isCarouselMode && currentAnnouncement ? (
          // Carousel Mode: Show single announcement
          <div className="flex-1 overflow-y-auto scroll-smooth px-4 sm:px-6 lg:px-10 py-4 sm:py-6 scrollbar-custom touch-pan-y overscroll-contain">
            <div className="space-y-3 sm:space-y-4">
              {/* Title and Severity Badge */}
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                <h3 className="text-base sm:text-lg font-semibold text-foreground flex-1 leading-snug">
                  {currentAnnouncement.title}
                </h3>
                <span
                  className={`inline-flex items-center rounded px-2 py-0.5 sm:py-1 text-xs font-medium border flex-shrink-0 ${severityConfig[currentAnnouncement.severity].className}`}
                >
                  {severityConfig[currentAnnouncement.severity].label}
                </span>
              </div>

              {/* Content */}
              <NewsMarkdownRenderer
                content={currentAnnouncement.content}
                className="prose-sm sm:prose text-foreground/90 [&_img]:mx-auto [&_img]:max-w-full [&_img]:max-h-[400px] sm:[&_img]:max-h-[500px] [&_img]:rounded-lg [&_img]:my-3 sm:[&_img]:my-4"
              />
            </div>
          </div>
        ) : (
          // List Mode: Show all announcements (scrollable)
          <div className="flex-1 overflow-y-auto scroll-smooth px-4 sm:px-6 lg:px-10 py-3 sm:py-4 scrollbar-custom touch-pan-y overscroll-contain">
            {announcements.length === 0 ? (
              <p className="text-sm text-muted-foreground">No news to display.</p>
            ) : (
              <div className="space-y-8">
                {(() => {
                  // Group announcements by time period
                  const now = new Date();
                  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const yesterdayStart = new Date(todayStart);
                  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

                  // Get the start of this week (Monday)
                  const thisWeekStart = new Date(todayStart);
                  const dayOfWeek = thisWeekStart.getDay();
                  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                  thisWeekStart.setDate(thisWeekStart.getDate() - daysFromMonday);

                  // Get the start of last week
                  const lastWeekStart = new Date(thisWeekStart);
                  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

                  // Get the start of this month
                  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

                  const groups: {
                    label: string;
                    announcements: Array<
                      (typeof announcements)[0] & {
                        displayLabel: string;
                        publishedDate: Date | null;
                      }
                    >;
                  }[] = [
                    { label: "Today", announcements: [] },
                    { label: "Yesterday", announcements: [] },
                    { label: "This Week", announcements: [] },
                    { label: "Last Week", announcements: [] },
                    { label: "This Month", announcements: [] },
                    { label: "Earlier", announcements: [] },
                  ];

                  announcements.forEach((announcement) => {
                    const publishedDate = announcement.publishedAt
                      ? new Date(announcement.publishedAt)
                      : null;

                    if (!publishedDate) return;

                    const dayName = publishedDate.toLocaleDateString("en-US", { weekday: "long" });
                    const formattedDate = publishedDate.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    });
                    const timeString = publishedDate.toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    });

                    const announcementWithLabel = {
                      ...announcement,
                      publishedDate,
                      displayLabel: "",
                    };

                    if (publishedDate >= todayStart) {
                      announcementWithLabel.displayLabel = `Today at ${timeString}`;
                      groups[0].announcements.push(announcementWithLabel);
                    } else if (publishedDate >= yesterdayStart) {
                      announcementWithLabel.displayLabel = `Yesterday at ${timeString}`;
                      groups[1].announcements.push(announcementWithLabel);
                    } else if (publishedDate >= thisWeekStart) {
                      announcementWithLabel.displayLabel = dayName;
                      groups[2].announcements.push(announcementWithLabel);
                    } else if (publishedDate >= lastWeekStart) {
                      announcementWithLabel.displayLabel = dayName;
                      groups[3].announcements.push(announcementWithLabel);
                    } else if (publishedDate >= thisMonthStart) {
                      announcementWithLabel.displayLabel = formattedDate;
                      groups[4].announcements.push(announcementWithLabel);
                    } else {
                      announcementWithLabel.displayLabel = formattedDate;
                      groups[5].announcements.push(announcementWithLabel);
                    }
                  });

                  return groups
                    .filter((group) => group.announcements.length > 0)
                    .map((group, groupIndex) => (
                      <div key={group.label}>
                        {/* Group Header */}
                        <div className="mb-3 flex items-center gap-3">
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                            {group.label}
                          </h4>
                          <div className="h-px flex-1 bg-border/30" />
                        </div>

                        {/* Announcements in Group */}
                        <div className="space-y-3 sm:space-y-4">
                          {group.announcements.map((announcement) => (
                            <div
                              key={announcement.id}
                              className={`rounded-lg bg-black/10 p-4 sm:p-5  ${severityConfig[announcement.severity].borderColor}`}
                            >
                              <div className="space-y-2 sm:space-y-3">
                                {/* Header: Title and Severity Badge */}
                                <div className="flex items-start justify-between gap-2 sm:gap-3">
                                  <h3 className="text-base sm:text-lg font-semibold text-foreground flex-1 leading-snug">
                                    {announcement.title}
                                  </h3>
                                  <span
                                    className={`inline-flex items-center rounded px-2 py-0.5 sm:py-1 text-xs font-medium border flex-shrink-0 ${severityConfig[announcement.severity].className}`}
                                  >
                                    {severityConfig[announcement.severity].label}
                                  </span>
                                </div>

                                {/* Metadata: Day Label */}
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{announcement.displayLabel}</span>
                                </div>

                                {/* Content */}
                                <NewsMarkdownRenderer
                                  content={announcement.content}
                                  className="prose-sm sm:prose text-foreground/90 pt-1 sm:pt-2 [&_img]:mx-auto [&_img]:max-w-full [&_img]:max-h-[400px] sm:[&_img]:max-h-[500px] [&_img]:rounded-lg [&_img]:my-3 sm:[&_img]:my-4"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                })()}
              </div>
            )}
          </div>
        )}

        {/* Footer with Navigation/Action Buttons */}
        <div className="border-t border-border/40 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex-shrink-0">
          {isCarouselMode ? (
            // Carousel Mode: Previous/Next navigation
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={isFirstAnnouncement}
                className="flex-1 sm:flex-initial sm:px-6 bg-muted hover:bg-accent border-border"
                aria-label="Previous announcement"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant={isLastAnnouncement ? "primary" : "outline"}
                onClick={handleNext}
                className={`flex-1 sm:flex-initial sm:min-w-[100px] ${
                  isLastAnnouncement ? "" : "bg-muted hover:bg-accent border-border"
                }`}
                aria-label={isLastAnnouncement ? "Got it" : "Next announcement"}
              >
                {isLastAnnouncement ? "Got it" : <ChevronRight className="h-5 w-5" />}
              </Button>
            </div>
          ) : (
            // List Mode: View All News link and Close button
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <Link
                href="/news"
                onClick={handleListAcknowledge}
                className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                View All News →
              </Link>
              <Button
                variant="primary"
                onClick={handleListAcknowledge}
                className="min-w-[80px] sm:min-w-[100px] text-sm"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
